import { FormEvent, useEffect, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { ICustomerLight } from '../../types/customer';
import { IMaterialInventory } from '../../types/material';
import { IMaterialOption, ISelectOption } from '../../types/list';

import { AppConfig } from '../../config/appConfig';
import { useListContext } from '../../contexts/listContext';
import { useUserContext } from "../../contexts/userContext";

import { CustomerService } from '../../services/customerService';
import { LoadService } from '../../services/loadService';
import { MaterialPriceService } from '../../services/materialPriceService';
import { UtilityService } from '../../services/utilityService';
import { VendorService } from '../../services/vendorService';

import NumberField from '../shared/numberField';
import Json from '../json';

import '../../styles/materialInventoryDetailsForm.scss';

const MaterialInventoryDetailsForm = () => {
	const navigate = useNavigate();
	const listContext = useListContext();
	const userContext = useUserContext();
	const defaultUomId = parseInt(listContext.unitsOfMeasurement.find(u => u.name == "pounds").value);
	const defaultContainerId = parseInt(listContext.containers.find(c => c.name == "None")?.id ?? listContext.containers[0].id);
	const [formFields, setFormFields] = useState<IMaterialInventory>({
		customerId: 0,
		vendorId: 0,
		materialId: 0,
		containerId: defaultContainerId,
		customerLocationId: 0,
		weight: 0,
		uomId: defaultUomId,
		quantity: undefined
	});
	const [isSaving, setIsSaving] = useState(false);
	const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
	const [isLoadingVendors, setIsLoadingVendors] = useState(true);
	const [isLoadingSources, setIsLoadingSources] = useState(true);
	const [isLoadingMaterialPrices, setIsLoadingMaterialPrices] = useState(true);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [customerOptions, setCustomerOptions] = useState<ISelectOption[]>([]);
	const [vendorOptions, setVendorOptions] = useState<ISelectOption[]>([]);
	const [materialOptions, setMaterialOptions] = useState<IMaterialOption[]>([]);
	const [filteredMaterialOptions, setFilteredMaterialOptions] = useState<IMaterialOption[]>([]);
	const [sourceOptions, setSourceOptions] = useState<ISelectOption[]>([]);
	const [selectedMaterialIndex, setSelectedMaterialIndex] = useState(0);
	const [previouslySelectedMaterialIndex, setPreviouslySelectedMaterialIndex] = useState(-1);

	const allowedTilsCustomerIds = userContext.tilsAccess.map(c => c.customerId);

	useEffect(() => {
		getCustomerOptions();
	}, [])

	useEffect(() => {
		if (formFields.customerId != 0) {
			getVendorOptions(formFields.customerId);
			getMaterialOptions();
			getSourceOptions();
		}
	}, [formFields.customerId]);

	useEffect(() => {
		if (!isLoadingVendors && !isLoadingMaterialPrices) {
			var filteredOptions = structuredClone(materialOptions.filter(item => formFields.vendorId == -1 || +item.vendorId == formFields.vendorId));
			annotateMaterialOptions(filteredOptions);
			setFilteredMaterialOptions(filteredOptions);
		}
	}, [isLoadingVendors, isLoadingMaterialPrices, formFields.vendorId]);

	useEffect(() => {
		if (filteredMaterialOptions.length > 0) {
			setSelectedMaterialIndex(0);
		}
	}, [filteredMaterialOptions])

	useEffect(() => {
		var option = getSelectedMaterialOption();
		var previousMaterial = previouslySelectedMaterialIndex > -1 ? materialOptions[previouslySelectedMaterialIndex] : undefined;
		if (option) {
			setFormFields({
				...
				formFields,
				["materialId"]: parseInt(filteredMaterialOptions[selectedMaterialIndex].tilsCode),
				["containerId"]: option.priceByContainer == true ? option.containerId : (previousMaterial?.priceByContainer == true ? defaultContainerId : formFields.containerId),
				["uomId"]: getUomIdFromName(option.uom) ?? defaultUomId,
				["quantity"]: option.priceByContainer ? 1 : undefined
			});
		}
		setPreviouslySelectedMaterialIndex(selectedMaterialIndex);
	}, [selectedMaterialIndex, filteredMaterialOptions])

	const getCustomerOptions = () => {
		setIsLoadingCustomers(true);
		CustomerService.getCustomers()
			.then((response) => {
				console.log('CustomerService.getCustomers: SUCCESS', response);
				const options: ISelectOption[] = [];
				const activeCustomers = CustomerService
					.mapCustomerLightList(response.data)
					.filter((item) => item.status == 'Active')
					.filter((item) => allowedTilsCustomerIds.includes(+item.id));

				activeCustomers.map((item: ICustomerLight) => {
					if (item.name) {
						options.push({
							"name": item.name,
							"value": item.id
						})
					}
				});
				setCustomerOptions(options.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));

				if (options.length > 0) {
					setFormFields({ ...formFields, ["customerId"]: parseInt(options[0].value) });
				}
			})
			.catch((error) => {
				console.log('CustomerService.getCustomers: ERROR', error);
			})
			.finally(() => {
				setIsLoadingCustomers(false);
			});
	}

	const getVendorOptions = (customerId: number) => {
		if (formFields.customerId == 0) return;
		setIsLoadingVendors(true);

		VendorService.getVendorsByCustomerId(customerId)
			.then((response) => {
				console.log('VendorService.getVendorsForCustomer: SUCCESS', response);
				let options: ISelectOption[] = VendorService.mapVendorLightList(response.data).map((item) => {
					return {
						"name": item.name,
						"value": item.id
					}
				});
				setVendorOptions(options.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));

				if (options.length > 0) {
					setFormFields({ ...formFields, ["vendorId"]: parseInt(options[0].value) });
				}
				else {
					setFormFields({ ...formFields, ["vendorId"]: -1 });
				}
			})
			.catch((error) => {
				console.log('VendorService.getVendorsForCustomer: ERROR', error);
			})
			.finally(() => {
				setIsLoadingVendors(false);
			});
	}

	const getMaterialOptions = () => {
		setIsLoadingMaterialPrices(true);
		setFormFields({ ...formFields, ["materialId"]: 0 });

		MaterialPriceService.getMaterialPricesForCustomer(formFields.customerId)
			.then((response) => {
				console.log('MaterialService.getMaterialsForCustomer: SUCCESS', response);
				const options = MaterialPriceService.mapMaterialPricesToMaterialOptions(MaterialPriceService.mapMaterialPrices(response.data));
				setMaterialOptions(options.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));

				if (options.length > 0) {
					setFormFields({ ...formFields, ["materialId"]: parseInt(options[0].value) });
				}
			})
			.catch((error) => {
				console.log('MaterialService.getMaterialsForCustomer: ERROR', error);
			})
			.finally(() => {
				setIsLoadingMaterialPrices(false);
			});
	}

	/**
	 *  Add annotations to material names if they are priced by container or if there are multiple prices for the same material
	 * @param options A list of IMaterialOptions to annotate
	 */
	const annotateMaterialOptions = (options: IMaterialOption[]) => {
		// Add the container name to each material that is priced by container
		for (let material of options.filter(o => o.priceByContainer == true)) {
			material.name = material.name + ` (by ${getContainerById(material.containerId)?.name ?? "container"})`;
		}

		// Differetiate between units of measure if there are material prices with the same material name priced by weight
		for (let materialId of Array.from(new Set(options.map(m => m.tilsCode)))) {
			const optionsForMaterial = options.filter(o => o.tilsCode == materialId.toString() && o.priceByContainer != true);
			if (optionsForMaterial.length > 1) {
				for (let material of optionsForMaterial) {
					material.name = material.name + ` (by ${material.uom})`;
				}
			}
		}
	}

	const getSourceOptions = () => {
		setIsLoadingSources(true);
		CustomerService.getActiveCustomerLocationsByCustomerId(formFields.customerId)
			.then((response) => {
				console.log('CustomerService.getActiveCustomerLocationsByCustomerId: SUCCESS', response);
				const locations = CustomerService.mapCustomerLocationOptions(response.data).sort((a, b) => UtilityService.compareStrings(a.name, b.name));
				setSourceOptions(locations);
			})
			.catch((error) => {
				console.log('CustomerService.getActiveCustomerLocationsByCustomerId: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsLoadingSources(false);
			});
	};

	const getContainerById = (id: number) => {
		return listContext.containers.find(c => c.id == id);
	}

	const getUomIdFromName = (name: string) => {
		return +listContext.unitsOfMeasurement.find(u => u.name == name)?.value || undefined;
	}

	const getSelectedMaterialOption = (): IMaterialOption | undefined => {
		return filteredMaterialOptions[selectedMaterialIndex];
	}

	const handleUomChange = (event: SelectChangeEvent<number>) => {
		const selectedUomId = Number.parseInt(event.target.value.toString());
		setFormFields({ ...formFields, ["uomId"]: selectedUomId });
	}

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setErrorMessage(undefined);
		setIsSaving(true);
		const inventory = { ...formFields, ['customerLocationId']: formFields.customerLocationId || undefined } as IMaterialInventory;

		LoadService.addMaterialInventory(inventory)
			.then((response) => {
				console.log('LoadService.addMaterialInventory: SUCCESS', response);
				const newInventory = response.data as IMaterialInventory;
				navigate("/materialinventory/" + newInventory.id);
			})
			.catch((error) => {
				console.log('LoadService.addMaterialInventory: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsSaving(false);
			});
	}

	return (
		<section className="hwi-material-inventory-details-form">
			{errorMessage &&
				<Alert className="alert-error"
					severity="error"
					action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
					{errorMessage}
				</Alert>
			}
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Form onSubmit={(event) => handleSubmit(event)}>
					<div className="row">
						<InputLabel>{(isLoadingCustomers) ? "Customer * (Loading...)" : "Customer *"}</InputLabel>
						<Select
							value={formFields.customerId < 1 ? '' : formFields.customerId}
							className="customer-field"
							required
							onChange={(event) => { setFormFields({ ...formFields, ["customerId"]: Number.parseInt(event.target.value.toString()) }) }}
							disabled={isLoadingCustomers}
						>
							{customerOptions.map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>{(isLoadingVendors) ? "Vendor * (Loading...)" : "Vendor *"}</InputLabel>
						<Select
							value={formFields.vendorId ?? ''}
							className="vendor-field"
							required
							onChange={(event) => { setFormFields({ ...formFields, ["vendorId"]: Number.parseInt(event.target.value.toString()) }) }}
							disabled={isLoadingVendors}
						>
							{/* <MenuItem value={-1} key={-1}>Unknown</MenuItem> */}
							{vendorOptions.map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>{(isLoadingMaterialPrices) ? "Material (Waste Category - Name) * (Loading...)" : "Material (Waste Category - Name) *"}</InputLabel>
						<Select
							value={selectedMaterialIndex}
							className="material-field"
							required
							onChange={(event) => { setSelectedMaterialIndex(parseInt(event.target.value.toString())) }}
							disabled={isLoadingMaterialPrices}
						>
							{filteredMaterialOptions.map((item, index) => (
								<MenuItem value={index} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>Container *</InputLabel>
						<Select
							value={formFields.containerId < 1 ? '' : formFields.containerId}
							className="container-field"
							required
							disabled={selectedMaterialIndex > -1 && getSelectedMaterialOption()?.priceByContainer == true}
							onChange={(event) => { setFormFields({ ...formFields, ["containerId"]: Number.parseInt(event.target.value.toString()) }) }}
						>
							{listContext.containers.map((item, index) => (
								<MenuItem value={item.id} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>
					{getSelectedMaterialOption()?.priceByContainer &&
						<div className="row">
							<InputLabel>Quantity *</InputLabel>
							<TextField className="quantity-field"
								name="quantity"
								type="number"
								required
								inputProps={{
									min: 1,
									step: "1"
								}}
								onBlur={(event) => { setFormFields({ ...formFields, ["quantity"]: parseInt(event.target.value) }) }}
							></TextField>
						</div>
					}
					<div className="row">
						<InputLabel>{(isLoadingSources) ? "Source (Location) (Loading...)" : "Source (Location)"}</InputLabel>
						<Select
							value={formFields.customerLocationId}
							className="source-field"
							onChange={(event) => { setFormFields({ ...formFields, ["customerLocationId"]: Number.parseInt(event.target.value.toString()) }) }}
							disabled={isLoadingSources}
						>
							<MenuItem value={0} key={0}>Select a Location</MenuItem>
							{sourceOptions.map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
						{(formFields.customerLocationId == -1) &&
							<p className="help-text">The selected customer does not have any associated inventory locations.</p>
						}
					</div>
					<div className="row">
						<InputLabel>Gross Weight *</InputLabel>
						<NumberField className="weight-field"
							name="weight"
							type="number"
							required
							min={0}
							step={1}
							precision={AppConfig.precisionDecimalsMaximum}
							onBlur={(event) => { setFormFields({ ...formFields, ["weight"]: parseFloat(event.target.value) }) }}
							onFocus={(event) => event.target.select()}
						></NumberField>
					</div>
					<div className="row">
						<InputLabel>Unit of Measure *</InputLabel>
						<Select
							value={formFields.uomId}
							className="unit-field"
							required
							disabled={selectedMaterialIndex > -1 && getSelectedMaterialOption()?.priceByContainer != true}
							onChange={handleUomChange}
						>
							{listContext.unitsOfMeasurement.map((item, index) => (
								<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row controls">
						<Button type="submit" className="save-button" variant="contained" disabled={isSaving}>{isSaving ? "Saving..." : "Save"}</Button>
					</div>
				</Form>
				{/* <Json data={formFields}></Json> */}
			</LocalizationProvider>
		</section>
	)
}

export default MaterialInventoryDetailsForm;